import { useLayoutEffect, useState } from "react";

/**
 * Live `getBoundingClientRect()` of the surface's content region — the anchor
 * the floating chat's fixed-position modes are computed from. Its `left`/`top`/
 * `height` stay valid even when the region is squeezed to ~0 width by a
 * full-width detail pane, so the left-docked chat / FAB can anchor to the
 * content area (after the left rail/explorer, below any chrome) without
 * hard-coding a header offset.
 *
 * `remeasureKey`: extra dep that forces a re-measure for position-only shifts a
 * ResizeObserver can't see (e.g. the left rail collapsing while the region's
 * width is pinned to the dock width).
 */
export function useAnchorRect(
  el: HTMLElement | null,
  remeasureKey?: unknown,
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!el) return;
    const measure = () => setRect(el.getBoundingClientRect());
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [el, remeasureKey]);
  return rect;
}
